import { stringValueOrEmpty } from "@tevari/helpers";
import Image from "next/image";
import { CSSProperties, FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";

import styles from "./Accordion.module.scss";

import { commonImages } from "@lib/images";

export type AccordionBorderPosition = "top" | "bottom" | "both";

export interface IAccordionProps {
  header: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
  buttonSize?: "big" | "small";
  lazy?: boolean;
  forceState?: "open" | "closed";
  onOpen?: () => void;
  onClose?: () => void;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  isLast?: boolean;
  testId?: string;
  unopenable?: boolean;
}

export const headerAlignmentToFlex: { [alignment: string]: "flexStart" | "center" | "flexEnd" } = {
  top: "flexStart",
  middle: "center",
  bottom: "flexEnd",
};

const Accordion: FunctionComponent<IAccordionProps> = ({
  header,
  children,
  style,
  buttonSize = "big",
  lazy = false,
  forceState,
  onOpen,
  onClose,
  className,
  headerClassName,
  contentClassName,
  disabled = false,
  isLast = false,
  testId,
  unopenable = false,
}) => {
  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const [displayedHeight, setDisplayedHeight] = useState(height);
  const contentRef = useRef<HTMLDivElement>(null);

  const _computeHeight = () => {
    const current = contentRef.current;
    if (!current) return 0;

    return contentRef.current.scrollHeight;
  };

  useEffect(() => {
    if (!contentRef.current) return;
    setHeight(_computeHeight());
    const resizeObserver = new ResizeObserver(() => setHeight(_computeHeight()));
    resizeObserver.observe(contentRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const _toggleOpen = () => {
    if (unopenable || disabled) return;

    setOpen((previous) => {
      const newState = !previous;
      if (newState) onOpen && onOpen();
      else onClose && onClose();
      return newState;
    });
  };

  useEffect(() => {
    setDisplayedHeight(open ? height : 0);
  }, [open, height]);

  useEffect(() => {
    if (forceState) setOpen(unopenable ? false : forceState === "open");
  }, [unopenable, forceState]);

  const _renderContent = () => {
    return (
      <div ref={contentRef}>
        <div>{lazy && !open ? <></> : children}</div>
      </div>
    );
  };

  const buttonClassName = open
    ? `${styles["expanded"]} ${styles["accordion__header__button"]}`
    : styles["accordion__header__button"];
  const buttonClassSize = stringValueOrEmpty(buttonSize === "small" && styles["accordion__header__button--small"]);
  const buttonIsLastClassName = stringValueOrEmpty(isLast && styles["last"]);
  const buttonDisabledClassName = stringValueOrEmpty((disabled || unopenable) && styles["disabled"]);

  return (
    <div
      style={style}
      className={`${styles["accordion"]} ${buttonIsLastClassName} ${stringValueOrEmpty(className)}`}
      data-testid={testId}
    >
      <div
        className={`${styles["accordion__header"]} ${buttonDisabledClassName} ${stringValueOrEmpty(headerClassName)}`}
        style={style}
        onClick={_toggleOpen}
      >
        <div className={`${styles["accordion__header__content"]}`}>{header}</div>
        {!unopenable && (
          <div className={`${buttonClassName} ${buttonClassSize}`}>
            <Image
              src={commonImages.chevron}
              alt="chevron"
              fill
              sizes="100vw"
              style={{
                objectFit: "contain",
              }}
            />
          </div>
        )}
      </div>
      <div
        className={`${styles["accordion__content"]} ${stringValueOrEmpty(open && styles["visible"])}  ${stringValueOrEmpty(contentClassName)}`}
        style={{ maxHeight: open ? displayedHeight : "0" }}
      >
        {_renderContent()}
      </div>
    </div>
  );
};

export default Accordion;
